import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import type { SchemaElement } from "../../../../api/restaurantSchema";
import { ELEMENT_SIZES, INSPECTOR_SEAT_CHIPS } from "../../../../components/restaurant-schema/elementCatalog";
import { MAX_SEATS } from "../../../../components/restaurant-schema/geometry";
import {
  Field,
  SeatsField,
  Segmented,
  TableShapeTiles,
  TextField,
} from "../modals/formControls";
import { hasTrailingStar } from "../../../../components/restaurant-schema/tableNames";
import type { SchemaEditorActions } from "../useSchemaEditorActions";
import { InspectorCard } from "./InspectorCard";

/**
 * Инспекторът на избраната маса (дизайн, т. 7) — вместо модал: номер, форма,
 * места, размер и завъртане се променят тук и се виждат веднага на схемата. Номерът
 * се записва при Enter/напускане на полето (не на всеки знак — 409 при зает).
 */
const TableInspector = ({
  table,
  actions,
  onDuplicate,
  onClose,
}: {
  table: SchemaElement;
  actions: SchemaEditorActions;
  onDuplicate: () => void;
  onClose: () => void;
}) => {
  const { t } = useTranslation();
  const [name, setName] = useState(table.name ?? "");
  const shape = table.tableShape ?? "round";
  const seats = table.seats ?? 4;

  // Промяна отвън (Отмени) → полето следва записа. Друга маса = нов инспектор
  // (родителят го монтира с `key` по id), затова текст не прескача между маси.
  const committedNameRef = useRef(table.name);
  // Отказан номер (409 — зает, маса с резервация/отворена сметка) връща кеша
  // към стария; нулираме и „последно записания", за да може същият номер да
  // се опита пак (напр. след като резервацията е преместена).
  useEffect(() => {
    setName(table.name ?? "");
    committedNameRef.current = table.name;
  }, [table.name]);

  // Старите маси с „*" не се мигрират — грешка само при ново име.
  const nameError =
    name.trim() !== table.name && hasTrailingStar(name) ? t("restaurantSchema.table.nameTrailingStar") : null;

  const commitName = () => {
    const trimmed = name.trim();
    if (!trimmed) {
      setName(table.name ?? "");
      return;
    }
    // „*" в края не се праща (BE 422) — текстът остава с грешката, за да се поправи.
    if (hasTrailingStar(trimmed)) return;
    if (trimmed !== table.name && trimmed !== committedNameRef.current) {
      committedNameRef.current = trimmed;
      actions.updateElement(table, { name: trimmed });
    }
  };

  // Клик по празното платно маха избора и демонтира инспектора, преди полето да
  // получи blur — тогава написаният номер се записва тук, иначе се губи.
  const commitOnUnmountRef = useRef(commitName);
  commitOnUnmountRef.current = commitName;
  useEffect(() => () => commitOnUnmountRef.current(), []);

  return (
    <InspectorCard
      badge={
        <span className="flex h-[38px] min-w-[38px] shrink-0 items-center justify-center rounded-[10px] bg-brand-tint px-1.5 text-[16px] font-bold text-brand-deep-ink">
          {table.name}
        </span>
      }
      title={t("restaurantSchema.table.title", { name: table.name })}
      subtitle={t("restaurantSchema.inspector.tableSubtitle", {
        shape: t(`restaurantSchema.tableShapes.${shape}`),
        seats: t("restaurantSchema.counts.seats", { count: seats }),
      })}
      rotation={table.rotation}
      onRotate={(direction) => actions.rotateElement(table, direction)}
      onDuplicate={onDuplicate}
      onDelete={() => void actions.deleteElement(table)}
      onClose={onClose}
    >
      <Field label={t("restaurantSchema.table.number")} error={nameError}>
        <TextField value={name} onChange={setName} onCommit={commitName} maxLength={50} compact />
      </Field>
      <Field label={t("restaurantSchema.table.shape")}>
        <TableShapeTiles
          value={shape}
          onChange={(tableShape) =>
            tableShape !== shape && actions.updateElement(table, { tableShape })
          }
          compact
        />
      </Field>
      <Field
        label={t("restaurantSchema.table.seats")}
        hint={t("restaurantSchema.inspector.seatsHint")}
      >
        <SeatsField
          value={seats}
          min={1}
          max={MAX_SEATS}
          chips={INSPECTOR_SEAT_CHIPS}
          onChange={(nextSeats) =>
            nextSeats !== seats && actions.updateElement(table, { seats: nextSeats })
          }
          compact
        />
      </Field>
      <Field label={t("restaurantSchema.table.size")}>
        <Segmented
          options={ELEMENT_SIZES.map((size) => ({
            value: size,
            label: t(`restaurantSchema.tableSizes.${size}`),
          }))}
          value={table.size}
          onChange={(size) => size !== table.size && actions.updateElement(table, { size })}
        />
      </Field>
    </InspectorCard>
  );
};

export default TableInspector;
